import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import BottomNav from '../components/BottomNav';
import CommentModal from '../components/CommentModal';
import ShareSheet from '../components/ShareSheet';
import { formatImageUrl } from '../utils/imageUtils';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'https://drvivara.cloud/api' });
api.interceptors.request.use(c => { const t = localStorage.getItem('token'); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });

const QUICK_EMOJIS = ['❤️', '🔥', '👏', '😮', '😢', '🙏'];

const ReelsPage = () => {
  const [reels, setReels] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [myRole, setMyRole] = useState('');
  const [showUI, setShowUI] = useState(true);
  const [showEndMessage, setShowEndMessage] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 0.5, y: 0.5 });
  const [commentReelId, setCommentReelId] = useState(null);
  const [shareReel, setShareReel] = useState(null);
  const [creatorInfo, setCreatorInfo] = useState(null);
  const [mediaLoaded, setMediaLoaded] = useState({});
  const [doubleTapHeart, setDoubleTapHeart] = useState(false);
  const [floatingHearts, setFloatingHearts] = useState([]);
  const [showEmojiBar, setShowEmojiBar] = useState(false);
  const [activeEmojiReelId, setActiveEmojiReelId] = useState(null);
  const containerRef = useRef(null);
  const videoRefs = useRef({});
  const lastTapRef = useRef(0);
  const doubleTapTimerRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const creatorId = searchParams.get('creatorId');

  const isCreator = ['doctor', 'hospital', 'clinic', 'retailer'].includes(myRole);

  const isYoutubeUrl = (url) => {
    if (!url) return false;
    return /youtube\.com|youtu\.be|youtube\.com\/shorts/.test(url);
  };

  const getYoutubeEmbedUrl = (url) => {
    const match = url.match(/(?:youtube\.com\/shorts\/|youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}?autoplay=1&loop=1&playlist=${match[1]}&mute=1&controls=0&playsinline=1` : null;
  };

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try { const u = JSON.parse(userStr); setMyRole(u.role); } catch (e) { }
    }
    fetchReels();
  }, [creatorId]);

  useEffect(() => {
    setCurrentIndex(0);
    setShowEndMessage(false);
    setZoom(1);
  }, [creatorId]);

  useEffect(() => { setMediaLoaded({}); }, [reels]);

  useEffect(() => {
    Object.keys(videoRefs.current).forEach(key => {
      const vid = videoRefs.current[key];
      if (!vid) return;
      if (Number(key) === currentIndex) {
        vid.play().catch(() => { });
      } else {
        vid.pause();
        vid.currentTime = 0;
      }
    });
  }, [currentIndex]);

  const markMediaLoaded = (index) => {
    setMediaLoaded(prev => ({ ...prev, [index]: true }));
  };

  const fetchReels = async () => {
    try {
      const endpoint = creatorId ? `/reels/creator/${creatorId}` : '/reels';
      const res = await api.get(endpoint);
      if (res.data.success) {
        setReels(res.data.data);
        if (creatorId && res.data.data?.length > 0) {
          setCreatorInfo(res.data.data[0].creator_id);
        } else {
          setCreatorInfo(null);
        }
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const goNext = useCallback(() => {
    if (currentIndex < reels.length - 1) {
      setCurrentIndex(i => i + 1);
      setShowEndMessage(false);
      setZoom(1);
      setShowEmojiBar(false);
    } else {
      setShowEndMessage(true);
    }
  }, [currentIndex, reels.length]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(i => i - 1);
      setShowEndMessage(false);
      setZoom(1);
      setShowEmojiBar(false);
    } else {
      setShowEndMessage(false);
    }
  }, [currentIndex]);

  const goToStart = useCallback(() => {
    setCurrentIndex(0);
    setShowEndMessage(false);
    setZoom(1);
  }, []);

  const [videoStuck, setVideoStuck] = useState({});
  const stuckTimersRef = useRef({});

  const clearStuckTimer = (idx) => {
    if (stuckTimersRef.current[idx]) {
      clearTimeout(stuckTimersRef.current[idx]);
      delete stuckTimersRef.current[idx];
    }
  };

  const startStuckTimer = (idx) => {
    clearStuckTimer(idx);
    setVideoStuck(prev => ({ ...prev, [idx]: false }));
    stuckTimersRef.current[idx] = setTimeout(() => {
      setVideoStuck(prev => ({ ...prev, [idx]: true }));
    }, 8000);
  };

  useEffect(() => {
    const idx = currentIndex;
    const reel = reels[idx];
    if (!reel) return;
    if (reel.media_url?.endsWith('.mp4')) {
      startStuckTimer(idx);
    }
    return () => clearStuckTimer(idx);
  }, [currentIndex, reels]);

  const handleVideoPlay = (idx) => {
    clearStuckTimer(idx);
    setVideoStuck(prev => ({ ...prev, [idx]: false }));
    markMediaLoaded(idx);
  };

  const retryVideo = (idx) => {
    const vid = videoRefs.current[idx];
    if (vid) {
      vid.load();
      vid.play().catch(() => { });
    }
    startStuckTimer(idx);
  };

  const triggerDoubleTapHeart = () => {
    setDoubleTapHeart(true);
    setTimeout(() => setDoubleTapHeart(false), 800);
  };

  const spawnFloatingHeart = (x, y) => {
    const id = Date.now() + Math.random();
    setFloatingHearts(prev => [...prev, { id, x, y }]);
    setTimeout(() => {
      setFloatingHearts(prev => prev.filter(h => h.id !== id));
    }, 1200);
  };

  const handleDoubleTapLike = (reel) => {
    if (!reel.liked) handleInteract(reel._id, 'like');
    triggerDoubleTapHeart();
    spawnFloatingHeart(
      40 + Math.random() * 20,
      60 + Math.random() * 20
    );
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [goNext, goPrev]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let startY = 0, startX = 0, startPinch = null;

    const onTouchStart = (e) => {
      startY = e.touches[0].clientY;
      startX = e.touches[0].clientX;
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        startPinch = Math.sqrt(dx * dx + dy * dy);
      } else {
        startPinch = null;
      }
    };

    const onTouchMove = (e) => {
      if (e.touches.length === 2 && startPinch) {
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const newZoom = Math.max(1, Math.min(5, zoom * (dist / startPinch)));
        setZoom(newZoom);
        const rect = el.getBoundingClientRect();
        const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2 / rect.width;
        const cy = (e.touches[0].clientY + e.touches[1].clientY) / 2 / rect.height;
        setZoomOrigin({ x: cx, y: cy });
      }
    };

    const onTouchEnd = (e) => {
      if (startPinch) { startPinch = null; return; }
      const diffY = startY - e.changedTouches[0].clientY;
      const diffX = Math.abs(startX - e.changedTouches[0].clientX);
      if (Math.abs(diffY) > 50 && diffX < Math.abs(diffY)) {
        if (diffY > 0) goNext();
        else goPrev();
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [goNext, goPrev, reels, currentIndex, zoom]);

  const handleWheel = (e) => {
    if (e.deltaY > 0) goNext();
    else if (e.deltaY < 0) goPrev();
  };

  const handleTap = (e) => {
    const now = Date.now();
    const timeSinceLastTap = now - lastTapRef.current;

    if (timeSinceLastTap < 300 && timeSinceLastTap > 0) {
      if (doubleTapTimerRef.current) clearTimeout(doubleTapTimerRef.current);
      handleDoubleTapLike(reels[currentIndex]);
      lastTapRef.current = 0;
      setShowEmojiBar(true);
      setActiveEmojiReelId(reels[currentIndex]._id);
      setTimeout(() => setShowEmojiBar(false), 3000);
      return;
    }

    lastTapRef.current = now;
    doubleTapTimerRef.current = setTimeout(() => {
      setShowUI(prev => !prev);
    }, 310);
  };

  const handleInteract = async (reelId, type, extra) => {
    try {
      const payload = { type };
      if (extra) Object.assign(payload, extra);
      const res = await api.post(`/reels/${reelId}/interact`, payload);
      if (res.data.success) {
        setReels(prev => prev.map(r =>
          r._id === reelId ? {
            ...r,
            liked: type === 'like' ? (res.data.data?.liked ?? !r.liked) : r.liked,
            saved: type === 'save' ? (res.data.data?.saved ?? !r.saved) : r.saved,
            like_count: type === 'like' ? (r.like_count || 0) + (res.data.data?.liked ? 1 : -1) : r.like_count,
            save_count: type === 'save' ? (r.save_count || 0) + (res.data.data?.saved ? 1 : -1) : r.save_count
          } : r
        ));
      }
    } catch (e) { if (e.response?.status !== 401) console.error(e); }
  };

  const handleEmojiReact = (emoji) => {
    if (activeEmojiReelId) {
      handleInteract(activeEmojiReelId, 'like');
    }
    setShowEmojiBar(false);
  };

  const handleComment = (reelId) => setCommentReelId(reelId);

  const formatCount = (n) => {
    if (!n) return '0';
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toString();
  };

  const SkeletonBlock = ({ className }) => (
    <div className={`bg-gray-800/50 rounded animate-pulse ${className}`} />
  );

  if (loading) return (
    <div className="bg-dark h-screen w-full fixed inset-0 overflow-hidden flex flex-col">
      <div className="shrink-0"><AppHeader /></div>
      <div className="flex-1 relative bg-black overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          <SkeletonBlock className="w-full h-full !rounded-none" />
        </div>
        <div className="absolute top-4 left-4 right-4 flex gap-1">
          {[1, 2, 3, 4, 5].map(i => <SkeletonBlock key={i} className="flex-1 h-0.5 !rounded-full" />)}
        </div>
        <div className="absolute right-3 bottom-24 flex flex-col items-center gap-5">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="flex flex-col items-center gap-1">
              <SkeletonBlock className="w-7 h-7 !rounded-full" />
              <SkeletonBlock className="w-6 h-3 !rounded" />
            </div>
          ))}
        </div>
      </div>
      <div className="shrink-0"><BottomNav /></div>
    </div>
  );

  const reel = reels[currentIndex];

  if (!reel) {
    return (
      <div className="bg-dark h-screen w-full fixed inset-0 overflow-hidden flex flex-col">
        <div className="shrink-0"><AppHeader /></div>
        <div className="flex-1 flex items-center justify-center text-gray-500">No reels available</div>
        <div className="shrink-0"><BottomNav /></div>
      </div>
    );
  }

  return (
    <div className="bg-dark h-screen w-full fixed inset-0 overflow-hidden flex flex-col">
      <div className="shrink-0">
        <AppHeader />
      </div>

      {/* Reel area */}
      <div className="flex-1 relative overflow-hidden bg-black min-h-0" ref={containerRef} style={{ touchAction: 'none' }}>

        {/* Media */}
        <div
          className="absolute inset-0 flex items-center justify-center"
          onWheel={handleWheel}
          onClick={handleTap}
        >
          {!mediaLoaded[currentIndex] && (
            <div className="absolute inset-0 flex items-center justify-center bg-black z-10">
              <div className="w-10 h-10 border-3 border-white/20 border-t-white rounded-full animate-spin" />
            </div>
          )}

          {reel.media_url?.endsWith('.mp4') ? (
            <video
              key={currentIndex}
              ref={el => videoRefs.current[currentIndex] = el}
              src={formatImageUrl(reel.media_url)}
              loop
              playsInline
              onLoadedData={() => markMediaLoaded(currentIndex)}
              onPlaying={() => handleVideoPlay(currentIndex)}
              onError={(e) => { e.target.style.display = 'none'; markMediaLoaded(currentIndex); }}
              className={`w-full h-full object-cover transition-transform duration-200 ${!mediaLoaded[currentIndex] ? 'opacity-0 absolute' : ''}`}
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: `${zoomOrigin.x * 100}% ${zoomOrigin.y * 100}%`
              }}
            />
          ) : isYoutubeUrl(reel.media_url) ? (
            <iframe
              key={currentIndex}
              src={getYoutubeEmbedUrl(reel.media_url)}
              className={`w-full h-full transition-transform duration-200 ${!mediaLoaded[currentIndex] ? 'opacity-0 absolute' : ''}`}
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: `${zoomOrigin.x * 100}% ${zoomOrigin.y * 100}%`,
                border: 'none'
              }}
              allow="autoplay; encrypted-media"
              onLoad={() => markMediaLoaded(currentIndex)}
              allowFullScreen
              title="Reel"
            />
          ) : (
            <img key={currentIndex} src={formatImageUrl(reel.media_url)} alt="Reel"
              onLoad={() => markMediaLoaded(currentIndex)}
              onError={(e) => { e.target.style.display = 'none'; markMediaLoaded(currentIndex); }}
              className={`w-full h-full object-cover transition-transform duration-200 ${!mediaLoaded[currentIndex] ? 'opacity-0 absolute' : ''}`}
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: `${zoomOrigin.x * 100}% ${zoomOrigin.y * 100}%`
              }}
            />
          )}
        </div>

        {/* Tap zones */}
        <div className="absolute inset-y-0 left-0 w-1/3 z-10" onClick={(e) => { e.stopPropagation(); goPrev(); }} />
        <div className="absolute inset-y-0 right-[72px] w-[calc(33%-72px)] z-10" onClick={(e) => { e.stopPropagation(); goNext(); }} />

        {/* Top progress bars */}
        <div className={`absolute top-3 left-3 right-3 z-20 flex gap-1 transition-opacity duration-300 ${showUI ? 'opacity-100' : 'opacity-0'}`}>
          {reels.map((_, i) => (
            <div key={i} className="flex-1 h-[2px] rounded-full bg-white/30 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${i < currentIndex ? 'bg-white w-full' : i === currentIndex ? 'bg-white' : 'w-0'}`}
                style={i === currentIndex ? { animation: `shrinkWidth 10s linear forwards` } : {}}
              />
            </div>
          ))}
        </div>

        {/* Back button + Upload */}
        <div className={`absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/60 via-black/20 to-transparent pt-10 pb-16 px-4 transition-opacity duration-300 ${showUI ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          <div className="flex items-center justify-between">
            <button onClick={() => { if (creatorId) navigate('/reels'); else navigate(-1); }} className="text-white p-1">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <p className="text-white font-semibold text-sm tracking-wide">Reels</p>
            {isCreator && (
              <button onClick={() => navigate('/reels/subscription')}
                className="bg-white/10 backdrop-blur-sm text-white p-1.5 rounded-lg">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Right side action buttons */}
        <div className={`absolute right-2 bottom-28 z-20 flex flex-col items-center gap-4 transition-opacity duration-300 ${showUI ? 'opacity-100' : 'opacity-0'}`}>
          {/* Creator avatar */}
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/reels/profile?creatorId=${reel.creator_id?._id || reel.creator_id}`); }}
            className="relative mb-1"
          >
            <div className="w-10 h-10 rounded-full bg-gold border-2 border-white flex items-center justify-center text-dark font-bold text-xs overflow-hidden">
              {reel.creator_id?.profile_image ? (
                <img src={formatImageUrl(reel.creator_id.profile_image)} alt="" className="w-full h-full object-cover" />
              ) : (
                reel.creator_id?.name?.charAt(0)?.toUpperCase() || 'U'
              )}
            </div>
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-4 h-4 bg-gold rounded-full flex items-center justify-center border border-white">
              <svg className="w-2.5 h-2.5 text-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
            </div>
          </button>

          {/* Like */}
          <button onClick={(e) => { e.stopPropagation(); handleInteract(reel._id, 'like'); }} className="flex flex-col items-center">
            <div className={`transition-transform duration-200 ${reel.liked ? 'scale-110' : 'active:scale-90'}`}>
              {reel.liked ? (
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="#ef4444">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                </svg>
              ) : (
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              )}
            </div>
            <span className="text-[11px] text-white font-semibold mt-0.5">{formatCount(reel.like_count)}</span>
          </button>

          {/* Comment */}
          <button onClick={(e) => { e.stopPropagation(); handleComment(reel._id); }} className="flex flex-col items-center">
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 20c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8c0 1.87.647 3.593 1.727 4.973L4 20l3.176-1.068A7.953 7.953 0 0012 20z" />
              <circle cx="9" cy="12" r="0.8" fill="white" />
              <circle cx="12" cy="12" r="0.8" fill="white" />
              <circle cx="15" cy="12" r="0.8" fill="white" />
            </svg>
            <span className="text-[11px] text-white font-semibold mt-0.5">{formatCount(reel.comment_count)}</span>
          </button>

          {/* Share */}
          <button onClick={(e) => { e.stopPropagation(); setShareReel(reel); }} className="flex flex-col items-center">
            <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
            </svg>
            <span className="text-[11px] text-white font-semibold mt-0.5">{formatCount(reel.share_count)}</span>
          </button>

          {/* Bookmark */}
          <button onClick={(e) => { e.stopPropagation(); handleInteract(reel._id, 'save'); }} className="flex flex-col items-center">
            <div className={`transition-transform duration-200 active:scale-90`}>
              {reel.saved ? (
                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="#D4AF37" stroke="#D4AF37" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              ) : (
                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              )}
            </div>
            <span className="text-[11px] text-white font-semibold mt-0.5">{formatCount(reel.save_count)}</span>
          </button>

          {/* Audio disc */}
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 border-2 border-gray-500 flex items-center justify-center mt-1 animate-spin-slow">
            <div className="w-2 h-2 rounded-full bg-white" />
          </div>
        </div>

        {/* Double-tap heart animation */}
        {doubleTapHeart && (
          <div className="absolute inset-0 z-30 pointer-events-none flex items-center justify-center">
            <svg className="w-28 h-28 drop-shadow-2xl animate-heartPop" viewBox="0 0 24 24" fill="#ef4444">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          </div>
        )}

        {/* Floating hearts on like */}
        {floatingHearts.map(h => (
          <div
            key={h.id}
            className="absolute z-30 pointer-events-none animate-floatUp"
            style={{ left: `${h.x}%`, bottom: '30%' }}
          >
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="#ef4444">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          </div>
        ))}

        {/* Quick emoji reaction bar */}
        <div className={`absolute left-1/2 -translate-x-1/2 bottom-44 z-30 flex gap-2 bg-black/40 backdrop-blur-md px-3 py-2 rounded-full transition-all duration-300 ${showEmojiBar ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-75 translate-y-4 pointer-events-none'}`}>
          {QUICK_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              onClick={(e) => { e.stopPropagation(); handleEmojiReact(emoji); }}
              className="text-2xl hover:scale-125 active:scale-90 transition-transform duration-150"
            >
              {emoji}
            </button>
          ))}
        </div>

        {/* Video stuck overlay */}
        {videoStuck[currentIndex] && (
          <div className="absolute inset-0 z-30 bg-black/60 flex flex-col items-center justify-center text-white">
            <div className="w-10 h-10 border-3 border-white/20 border-t-white rounded-full animate-spin mb-3" />
            <p className="text-sm font-medium mb-3">Taking longer than usual</p>
            <button onClick={(e) => { e.stopPropagation(); retryVideo(currentIndex); }}
              className="bg-white text-dark px-5 py-2 rounded-lg font-semibold text-sm active:scale-95 transition">
              Retry
            </button>
          </div>
        )}

        {/* End of posts overlay */}
        {showEndMessage && (
          <div className="absolute inset-0 z-30 bg-black/80 flex flex-col items-center justify-center text-white">
            <p className="text-xl font-bold mb-1">You're all caught up</p>
            <p className="text-sm text-gray-400 mb-6">You've seen all new posts from the last 3 days.</p>
            <button onClick={goToStart}
              className="bg-white text-dark px-8 py-2.5 rounded-lg font-semibold text-sm active:scale-95 transition">
              Watch again
            </button>
          </div>
        )}

        {/* Bottom info overlay */}
        <div className={`absolute bottom-14 left-0 right-[72px] z-20 px-4 pb-3 transition-opacity duration-300 ${showUI ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          {/* Creator + Follow */}
          <div className="flex items-center gap-2.5 mb-2.5">
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/reels/profile?creatorId=${reel.creator_id?._id || reel.creator_id}`); }}
              className="flex items-center gap-2.5 flex-1 min-w-0"
            >
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gold to-amber-600 flex items-center justify-center text-dark font-bold text-xs overflow-hidden ring-2 ring-white/20 shrink-0">
                {reel.creator_id?.profile_image ? (
                  <img src={formatImageUrl(reel.creator_id.profile_image)} alt="" className="w-full h-full object-cover" />
                ) : (
                  reel.creator_id?.name?.charAt(0)?.toUpperCase() || 'U'
                )}
              </div>
              <p className="text-sm text-white font-semibold truncate">{reel.creator_id?.name || 'Unknown'}</p>
            </button>
            <span className="text-white/40 mx-1">•</span>
            <p className="text-xs text-white/50 shrink-0">{formatCount(reel.view_count || 0)} views</p>
          </div>

          {/* Caption */}
          {reel.caption && (
            <p className="text-[13px] text-white/90 leading-snug line-clamp-2 mb-1.5">{reel.caption}</p>
          )}

          {/* Music ticker */}
          <div className="flex items-center gap-2">
            <svg className="w-3 h-3 text-white/60 shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
            </svg>
            <div className="overflow-hidden flex-1">
              <p className="text-xs text-white/60 truncate">{reel.creator_id?.name || 'Unknown'} • Dr. Vivara Reels</p>
            </div>
          </div>
        </div>
      </div>

      <div className="shrink-0">
        <BottomNav />
      </div>

      {commentReelId && <CommentModal reelId={commentReelId} onClose={() => setCommentReelId(null)} api={api} />}
      {shareReel && <ShareSheet reel={shareReel} onClose={() => setShareReel(null)} onShare={(id, type) => handleInteract(id, type)} />}
    </div>
  );
};

export default ReelsPage;
